/**
 * React Query hooks for the clustering service.
 * All hooks assume a QueryClientProvider is mounted upstream (see App.tsx).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import * as api from '../api/clustering';
import { TERMINAL_BATCH_STATUSES } from '../types/clustering';
import type {
  BatchCreate,
  BatchRead,
  BatchSummaryRead,
  BatchesListParams,
  ClusterRead,
  ClusterRename,
  ClustersListParams,
  EstimateRequest,
  EstimateResult,
  ExportFormat,
  KeywordRead,
  KeywordsListParams,
  ProgressEvent,
  ProjectCreate,
  ProjectRead,
  ProjectUpdate,
  ReclusterRequest,
} from '../types/clustering';

export const clusteringKeys = {
  all: ['clustering'] as const,
  projects: () => [...clusteringKeys.all, 'projects'] as const,
  project: (id: string) => [...clusteringKeys.projects(), id] as const,
  batches: (params?: BatchesListParams) =>
    [...clusteringKeys.all, 'batches', params ?? {}] as const,
  batch: (id: string) => [...clusteringKeys.all, 'batch', id] as const,
  summary: (id: string) => [...clusteringKeys.batch(id), 'summary'] as const,
  clusters: (batchId: string, params?: ClustersListParams) =>
    [...clusteringKeys.batch(batchId), 'clusters', params ?? {}] as const,
  keywords: (batchId: string, params?: KeywordsListParams) =>
    [...clusteringKeys.batch(batchId), 'keywords', params ?? {}] as const,
};

// --- Projects --------------------------------------------------------------

export function useClusteringProjects(
  options?: Omit<UseQueryOptions<ProjectRead[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ProjectRead[]>({
    queryKey: clusteringKeys.projects(),
    queryFn: api.listProjects,
    ...options,
  });
}

export function useClusteringProject(id: string | undefined) {
  return useQuery<ProjectRead>({
    queryKey: clusteringKeys.project(id ?? ''),
    queryFn: () => api.getProject(id!),
    enabled: !!id,
  });
}

export function useCreateClusteringProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectCreate) => api.createProject(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: clusteringKeys.projects() }),
  });
}

export function useUpdateClusteringProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: ProjectUpdate }) =>
      api.updateProject(vars.id, vars.body),
    onSuccess: (proj) => {
      qc.invalidateQueries({ queryKey: clusteringKeys.projects() });
      qc.setQueryData(clusteringKeys.project(proj.id), proj);
    },
  });
}

export function useDeleteClusteringProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: clusteringKeys.projects() }),
  });
}

// --- Batches ---------------------------------------------------------------

export function useClusteringBatches(params?: BatchesListParams) {
  return useQuery<BatchRead[]>({
    queryKey: clusteringKeys.batches(params),
    queryFn: () => api.listBatches(params),
    enabled: params?.project_id === undefined || !!params.project_id,
  });
}

export function useEstimateBatch() {
  return useMutation<EstimateResult, Error, EstimateRequest>({
    mutationFn: (body) => api.estimateBatch(body),
  });
}

export function useCreateBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchCreate) => api.createBatch(body),
    onSuccess: (batch) => {
      qc.invalidateQueries({ queryKey: clusteringKeys.all });
      qc.setQueryData(clusteringKeys.batch(batch.id), batch);
    },
  });
}

export function useRerunBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.rerunBatch(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: clusteringKeys.all }),
  });
}

export function useReclusterBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReclusterRequest }) =>
      api.reclusterBatch(id, body),
    onSuccess: (batch) => {
      qc.invalidateQueries({ queryKey: clusteringKeys.all });
      qc.setQueryData(clusteringKeys.batch(batch.id), batch);
    },
  });
}

export function useCancelBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.cancelBatch(id),
    onSuccess: (batch) => {
      qc.setQueryData(clusteringKeys.batch(batch.id), batch);
      qc.invalidateQueries({ queryKey: clusteringKeys.all });
    },
  });
}

export function useDeleteBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteBatch(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: clusteringKeys.all }),
  });
}

export function useClusteringBatchSummary(batchId: string | undefined, enabled = true) {
  return useQuery<BatchSummaryRead>({
    queryKey: clusteringKeys.summary(batchId ?? ''),
    queryFn: () => api.getBatchSummary(batchId!),
    enabled: !!batchId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 3000 : false;
    },
  });
}

export function useRegenerateBatchSummary(batchId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.regenerateBatchSummary(batchId),
    onSuccess: (summary) => {
      qc.setQueryData(clusteringKeys.summary(batchId), summary);
      qc.invalidateQueries({ queryKey: clusteringKeys.summary(batchId) });
    },
  });
}

// --- Single batch + live progress -----------------------------------------

export interface UseClusteringBatchResult {
  batch: BatchRead | undefined;
  progress: ProgressEvent | null;
  isLoading: boolean;
  isTerminal: boolean;
  error: Error | null;
}

/**
 * Hook backing the batch detail page.
 *
 * Fetches the batch once via React Query, subscribes to `/stream` for live
 * updates while the batch is non-terminal, and flushes the query cache +
 * cluster list once the batch completes.
 */
export function useClusteringBatch(id: string | undefined): UseClusteringBatchResult {
  const qc = useQueryClient();
  const [progress, setProgress] = useState<ProgressEvent | null>(null);
  const invalidatedRef = useRef<string | null>(null);

  const query = useQuery<BatchRead>({
    queryKey: clusteringKeys.batch(id ?? ''),
    queryFn: () => api.getBatch(id!),
    enabled: !!id,
  });

  const subRef = useRef<api.ProgressSubscription | null>(null);

  useEffect(() => {
    if (!id) return;
    const currentStatus = query.data?.status;
    if (currentStatus && TERMINAL_BATCH_STATUSES.has(currentStatus)) {
      // Guard against race conditions where the SSE onComplete may not fire
      // because polling updated status to terminal before the stream closed.
      if (invalidatedRef.current !== id) {
        invalidatedRef.current = id;
        qc.invalidateQueries({ queryKey: [...clusteringKeys.batch(id), 'clusters'] });
        qc.invalidateQueries({ queryKey: [...clusteringKeys.batch(id), 'keywords'] });
      }
      return;
    }
    invalidatedRef.current = null;
    subRef.current = api.subscribeBatchProgress(id, {
      onEvent: (e) => {
        setProgress(e);
        qc.setQueryData<BatchRead | undefined>(clusteringKeys.batch(id), (prev) =>
          prev
            ? {
                ...prev,
                status: e.status,
                current_step: e.current_step,
                progress_pct: e.progress_pct,
                serp_cache_hits: e.serp_cache_hits,
                serp_cache_misses: e.serp_cache_misses,
                clusters_count: e.clusters_count ?? prev.clusters_count,
              }
            : prev,
        );
      },
      onComplete: () => {
        invalidatedRef.current = id;
        qc.invalidateQueries({ queryKey: clusteringKeys.batch(id) });
        qc.invalidateQueries({ queryKey: [...clusteringKeys.batch(id), 'clusters'] });
        qc.invalidateQueries({ queryKey: [...clusteringKeys.batch(id), 'keywords'] });
      },
    });
    return () => {
      subRef.current?.close();
      subRef.current = null;
    };
  }, [id, query.data?.status, qc]);

  return {
    batch: query.data,
    progress,
    isLoading: query.isLoading,
    isTerminal: !!query.data && TERMINAL_BATCH_STATUSES.has(query.data.status),
    error: (query.error as Error | null) ?? null,
  };
}

// --- Clusters + keywords ---------------------------------------------------

export function useClusteringClusters(
  batchId: string | undefined,
  params?: ClustersListParams,
) {
  return useQuery<ClusterRead[]>({
    queryKey: clusteringKeys.clusters(batchId ?? '', params),
    queryFn: () => api.listClusters(batchId!, params),
    enabled: !!batchId,
  });
}

export function useClusteringKeywords(
  batchId: string | undefined,
  params?: KeywordsListParams,
) {
  return useQuery<KeywordRead[]>({
    queryKey: clusteringKeys.keywords(batchId ?? '', params),
    queryFn: () => api.listKeywords(batchId!, params),
    enabled: !!batchId,
  });
}

export function useRenameCluster(batchId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { clusterId: string; body: ClusterRename }) =>
      api.renameCluster(vars.clusterId, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...clusteringKeys.batch(batchId), 'clusters'] });
    },
  });
}

// --- Export ----------------------------------------------------------------

export function useBatchExport() {
  return useMutation<void, Error, { batchId: string; format: ExportFormat; filename?: string }>({
    mutationFn: async ({ batchId, format, filename }) => {
      const blob = await api.downloadBatchExport(batchId, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? `batch-${batchId}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}
